import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse, Info, PhoneCall } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import LoadFailureNote from "./LoadFailureNote";
import api, { formatApiErrorDetail } from "../lib/api";
import { resolveSaveErrorMessage } from "../lib/settingsErrorMessages";
import {
  CONDITIONS_MAX,
  CONTACT_ALT_MAX,
  CONTACT_NAME_MAX,
  CONTACT_PHONE_MAX,
  CONTACT_RELATIONSHIP_MAX,
  EMPTY_HEALTH_FORM,
  NEEDS_MAX,
  NOT_PROVIDED,
  SHARING_DETAIL,
  SHARING_NOTICE,
  describeHealthAudience,
  formatAuditLine,
  hasAnyHealthContent,
  toHealthForm,
  toHealthPayload,
} from "../lib/playerHealth";
import { SectionCard } from "./identityForm";
import { healthSummary } from "../lib/settingsSummary";

/**
 * "Health and emergency contact" — the player's own entry card, in Settings
 * (issue #287), where Martin asked for it: *"Under your account settings
 * should also be fields outlining any medical conditions that the player
 * might have or disabilities that a club or coach need to be aware."* —
 * and then, 2026-08-18: *"For medical & disability add in emergency contact
 * person and contact details as well."*
 *
 * ITS OWN CARD, NOT ROWS ON THE ACCOUNT CARD ABOVE. The sharing statement
 * has to sit on the health data and on nothing else: a notice at the top of
 * the page would either be read as covering the display name too (it does
 * not) or be tuned out entirely. Separate card, separate statement,
 * separate save.
 *
 * PLAYER ROLE ONLY — `Settings.jsx` gates the render, the same way it gates
 * `CoachAccessCard` and `CoachProfileCard`. A coach has no player profile,
 * so the API answers 404 on `/players/me/health`; showing them an empty
 * health form they can never save is #283's defect exactly.
 *
 * ============================================================
 * EMERGENCY CONTACT FIRST. THAT ORDER IS A DECISION.
 * ============================================================
 * Martin, 2026-08-18: *"emergency contact first in the card, conditions and
 * accessibility needs below. It is the field most likely to be needed in a
 * hurry and the least sensitive of the three."* The coach's panel renders
 * in the same order, off the same `HEALTH_FIELDS` constant, so a coach who
 * has learned where to look finds it where the player left it.
 *
 * Four fields, not one blob: name · relationship · phone · one optional
 * alternative. Relationship is FREE TEXT and not a dropdown, same reasoning
 * as the conditions field — the first family that does not fit "parent /
 * guardian / partner" is the one it matters for. The alternative field is
 * deliberately untyped: a second number or an email, and the form does not
 * ask which.
 *
 * ============================================================
 * THE COPY IS THE FEATURE. Do not shorten it.
 * ============================================================
 *   - `SHARING_NOTICE` is on the card, above the first field, before
 *     anything is typed, and it NAMES the emergency contact: sharing is
 *     automatic to every ACTIVE linked coach with no per-coach control, and
 *     the thing being shared includes somebody else's phone number.
 *   - The third-party line under the contact fields says so outright. The
 *     person being named here has no account and has consented to nothing;
 *     the player is the only one in a position to think about that, and
 *     only if we say it.
 *   - The blank-fields line names what a coach actually sees for an empty
 *     field (`NOT_PROVIDED`) and says explicitly that it is not read as
 *     "none".
 *   - Nothing here says "private", "secure" or "only your coach will ever
 *     see this". An operator with database access can read these rows and
 *     #223's nightly backup lands in a GitHub Actions artifact; the copy
 *     may not imply a guarantee the system does not make. Same honesty rule
 *     #205 wrote for coach reflections.
 *
 * NOTHING HERE IS REQUIRED, AND NOTHING GATES ON IT. There is no asterisk,
 * no "complete your profile" nudge, and the #289 onboarding walkthrough
 * does not mention this card. A mandatory health field produces a filled-in
 * form, not accurate information — and the empty state has to stay
 * meaningfully empty for "Not provided" to mean anything.
 *
 * ============================================================
 * WHO, BY NAME — issue #694, following #680
 * ============================================================
 * `SHARING_NOTICE` states the RULE ("your linked coaches can see this");
 * `describeHealthAudience` (`lib/playerHealth.js`) states who that resolves
 * to RIGHT NOW, by name, server-resolved — the same obligation #680 already
 * met for Identity & Vision (`IdentitySharingCard.jsx`'s `describeVisibility`
 * sentence). The health record is the more sensitive of the two surfaces
 * and has no opt-in at all, so it is the one that most needs the audience
 * to be visible rather than inferred: a player with a club coach and a
 * private coach must be able to read, in one sentence, that BOTH are in the
 * audience for their medical conditions and their parent's phone number.
 *
 * RENDERED ONLY AFTER A SUCCESSFUL LOAD (#271). `saved` is `null` until the
 * fetch resolves, and stays `null` on a failed one (see `load` below), so
 * gating on `saved` is what stops this card from ever saying "Nobody can
 * see this" as a guess. A failed load shows `LoadFailureNote` INSTEAD —
 * never the audience sentence with an empty names list, which would read as
 * confirmed safety rather than as "we don't know".
 *

 * ============================================================
 * IT FOLDS (#456), AND THE SHARING NOTICE STAYS INSIDE THE FOLD.
 * ============================================================
 * Martin asked for this section to collapse. It arrives CLOSED, like the
 * other four folded settings sections, which means `SHARING_NOTICE` is not
 * on screen until the card is opened — and that is deliberate rather than
 * an oversight of the copy rule above. The notice exists to be read AT THE
 * MOMENT somebody decides what to type, and there is no way to type into
 * this card without opening it first. A player who never opens it has
 * shared nothing, because there is nothing here to share.
 *
 * The header state answers the one question a folded health card has to
 * answer — is there anything in here? — from the SAVED record, not the
 * form, because the badge is about what a coach can see. See
 * `healthSummary` in `lib/settingsSummary.js` for why "Incomplete" is not
 * one of its words on a card where nothing is required.
 *
 * SAVE MODEL: edit-in-place with an explicit Save, matching the Account and
 * coaching-profile cards. Save is enabled whenever the form differs from
 * what is stored, INCLUDING when the difference is that the player has
 * blanked everything — clearing is a save (#287 §5, day-one requirement;
 * true erasure waits on #253), so the control must not grey out at exactly
 * the moment somebody is trying to remove their child's health record.
 *
 * Issue #976 (D186.7, D186.10's sibling for Health) — `appearance="r5"`
 * (already threaded through to `SectionCard` for the frame, Settings A step
 * 6) ALSO restyles this card's own children now that Settings B opens the
 * fold on screen: the sharing notice, drawn `SettingsHealth.dc.html`
 * comment says outright, is "a white card with a navy rule: the gold is the
 * one action on the page (R-003), and here that is Save details" — i.e. the
 * gold-tinted box this card ships (`border-[#D4A017]/40 bg-[#D4A017]/5`) is
 * the SAME kind of divergence the former `AccountProfileCardNew` (#1135) documents for
 * Sign out everywhere: the shipped colour is not the r5 token, and r5 wins.
 * `health-blank-meaning`'s `text-slate-600` is the D194 class of bug —
 * the platform-wide WCAG bump renders it `#334155`, not the drawn `#475569`
 * (`var(--nw-ink-lede)`). `health-save` goes gold under r5 (D186.7 names
 * "Save details" explicitly); `health-clear` stays the navy outline it
 * already is, unchanged (not on D55's destructive list — it only stages a
 * blank form, Save is still the write). The shipped call
 * (`pages/Settings.jsx`'s bare `<HealthInfoCard />`, no `appearance`) is
 * untouched — every branch below keys off `appearance === "r5"`.
 *
 * #989 round 2 fix (F2, D195): "Save details" stays gold under r5 — D195
 * confirms the opened fold's own commit is its frame's one gold, following
 * D186.7 as far as D64 allows. `health-clear` becomes the drawn navy
 * `#0B2E59` outline on a `#CBD5E1` border under r5 (a screen gap, not a
 * destructive control — D55 doesn't reach it). `health-audience-state`
 * moves from `text-slate-700`/`font-medium` to the drawn `#0F172A`/600
 * weight under r5. A save failure now renders on the page (D57) via the
 * shared `AlertCallout` shape, `health-save-error`, in the drawn words
 * ("Save failed", `SettingsHealth.dc.html`'s own `save-failed` state) —
 * never a toast under r5.
 */
const HEALTH_GOLD = "#D4A017";
const HEALTH_INK = "#0F172A";
const HEALTH_NAVY = "#0B2E59";
const HEALTH_ALERT = "#B42318";

/** D57's on-page failure shape (round 2 review, S-F1) — kept as its own
 * local copy, matching this codebase's stated preference over a shared
 * import for a ten-line block a handful of files use. */
function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: `3px solid ${HEALTH_ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "#0F172A",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color={HEALTH_ALERT} />
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function HealthInfoCard({ appearance } = {}) {
  const r5 = appearance === "r5";
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // D57 (#989 round 2, F4) — only read under r5; the shipped leg still
  // toasts and never sets this.
  const [saveError, setSaveError] = useState("");

  const load = () => {
    setFailed(false);
    return api
      .get("/players/me/health")
      .then((r) => {
        setSaved(r.data);
        setForm(toHealthForm(r.data));
      })
      .catch(() => {
        // #271: a swallowed failure must never render as an empty form.
        // On THIS card that lie is worse than usual — an empty form reads
        // as "you never told us about the asthma", and the obvious response
        // is to type it in again over the top of a record we simply failed
        // to fetch.
        setFailed(true);
        setForm(null);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Compared as PAYLOADS, not as form state: `""` here and `null` from the
  // API are the same instruction, and a raw comparison would call the form
  // dirty forever after one save.
  const dirty =
    form && saved && JSON.stringify(toHealthPayload(form)) !== JSON.stringify(toHealthPayload(toHealthForm(saved)));

  const auditLine = formatAuditLine(saved);

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const { data } = await api.put("/players/me/health", toHealthPayload(form));
      setSaved(data);
      setForm(toHealthForm(data));
      toast.success("Health details saved");
    } catch (e) {
      // D57 (#989 round 2, F4): on the page under r5, drawn words ("Save
      // failed"), AlertCallout shape, no toast. Shipped leg keeps the
      // toast and its own (unfixed, byte-identical) message computation —
      // `resolveSaveErrorMessage`'s O1 fix (#989 round 3) is scoped to r5
      // only, same as every other branch in this file.
      if (r5) {
        setSaveError(resolveSaveErrorMessage(e));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      }
    }
    setSaving(false);
  };

  const clearAll = () => setForm({ ...EMPTY_HEALTH_FORM });

  return (
    <SectionCard
      icon={HeartPulse}
      title="Health and emergency contact"
      desc="Who to call, and anything a coach should know before a session — conditions, allergies, access needs. All of it is optional."
      collapsible
      defaultOpen={false}
      summary={healthSummary({ failed, record: saved })}
      toggleTestid="settings-health-toggle"
      usageItem="health"
      testid="settings-health-card"
      appearance={appearance}
    >
      {/* The sharing statement, on the card, before the first field.
          `r5`: a white card with a navy left rule (`SettingsHealth.dc.html`'s
          own words — "the gold is the one action on the page ... here that
          is Save details"), not the shipped gold-tinted box. */}
      <div
        className={
          r5
            ? "rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-2.5 min-w-0"
            : "rounded-xl border-2 border-[#D4A017]/40 bg-[#D4A017]/5 p-3 flex items-start gap-2.5 min-w-0"
        }
        style={r5 ? { borderLeft: `3px solid ${HEALTH_NAVY}` } : undefined}
        data-testid="health-sharing-notice"
      >
        <Info size={16} color={r5 ? HEALTH_NAVY : "#8a6a0f"} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 break-words">{SHARING_NOTICE}</p>
          {/* WHO, by name, right now (#694) — only once the load has actually
              succeeded (#271). `saved` stays null on a failed fetch, so this
              can never render a guess as "Nobody can see this". */}
          {!failed && saved && (
            // Screen gap (#989 round 2): #0F172A/600 under r5, not the
            // shipped slate-700/font-medium approximation.
            <p
              className={r5 ? "text-xs mt-1 break-words font-semibold" : "text-xs text-slate-700 font-medium mt-1 break-words"}
              style={r5 ? { color: HEALTH_INK } : undefined}
              data-testid="health-audience-state"
            >
              {describeHealthAudience({ coachNames: saved.coach_names, hasContent: hasAnyHealthContent(saved) })}
            </p>
          )}
          {/* N1 (#989 round 2): var(--nw-ink-lede) under r5, not
              text-slate-600 (the D194 class of bug). */}
          <p
            className={r5 ? "text-xs mt-1 break-words" : "text-xs text-slate-600 mt-1 break-words"}
            style={r5 ? { color: "var(--nw-ink-lede)" } : undefined}
          >
            {SHARING_DETAIL}
          </p>
          {/* D194-class fix under r5: `text-slate-600` computes to #334155
              (the platform-wide WCAG bump), not the drawn #475569
              (`var(--nw-ink-lede)`). Shipped leg unchanged. */}
          <p
            className={r5 ? "text-xs mt-1 break-words" : "text-xs text-slate-600 mt-1 break-words"}
            style={r5 ? { color: "var(--nw-ink-lede)" } : undefined}
            data-testid="health-blank-meaning"
          >
            Anything you leave blank shows to them as "{NOT_PROVIDED}" — we never tell a coach you have
            nothing to report.
          </p>
        </div>
      </div>

      {failed && (
        <LoadFailureNote what="your health details" onRetry={load} testId="health-load-failed" />
      )}

      {form && (
        <>
          {/* FIRST, per Martin's ordering decision. */}
          <div>
            <div className="flex items-center gap-2">
              <PhoneCall size={14} color="#0B2E59" />
              <div className="font-semibold text-slate-900 text-sm">Emergency contact</div>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Who a coach should call at a ground if something happens.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div>
                <Label htmlFor="health-contact-name">Name</Label>
                <Input
                  id="health-contact-name"
                  data-testid="health-contact-name"
                  value={form.emergency_contact_name}
                  onChange={(e) => set("emergency_contact_name", e.target.value)}
                  maxLength={CONTACT_NAME_MAX}
                  placeholder="e.g. Jo Whitfield"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="health-contact-relationship">Relationship to you</Label>
                <Input
                  id="health-contact-relationship"
                  data-testid="health-contact-relationship"
                  value={form.emergency_contact_relationship}
                  onChange={(e) => set("emergency_contact_relationship", e.target.value)}
                  maxLength={CONTACT_RELATIONSHIP_MAX}
                  // Free text, not a select: "parent / guardian / partner
                  // / other" is a list that fails the first family it does
                  // not describe, and this is the field where being told
                  // "other" helps nobody at a ground.
                  placeholder="e.g. Mum, guardian, partner"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="health-contact-phone">Phone</Label>
                <Input
                  id="health-contact-phone"
                  data-testid="health-contact-phone"
                  value={form.emergency_contact_phone}
                  onChange={(e) => set("emergency_contact_phone", e.target.value)}
                  maxLength={CONTACT_PHONE_MAX}
                  // `type="text"`, not `type="tel"` with a pattern: the
                  // number is stored exactly as typed and never dialled by
                  // this app, and a validation rule that rejects an
                  // overseas grandparent's number would be worse than no
                  // rule at all.
                  placeholder="e.g. 021 555 0134"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="health-contact-alt">Another number or email (optional)</Label>
                <Input
                  id="health-contact-alt"
                  data-testid="health-contact-alt"
                  value={form.emergency_contact_alt}
                  onChange={(e) => set("emergency_contact_alt", e.target.value)}
                  maxLength={CONTACT_ALT_MAX}
                  placeholder="e.g. 027 555 9911 or jo@example.com"
                  className="mt-1.5 h-11"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-2 break-words" data-testid="health-third-party-note">
              This is someone else's contact detail. They don't have an account here and we never contact
              them — your linked coaches simply see what you've written, so make sure they'd be happy for
              you to share it.
            </p>
          </div>

          <Separator />

          <div>
            <Label htmlFor="health-conditions">Medical conditions and allergies</Label>
            <Textarea
              id="health-conditions"
              data-testid="health-conditions"
              value={form.medical_conditions}
              onChange={(e) => set("medical_conditions", e.target.value)}
              maxLength={CONDITIONS_MAX}
              rows={4}
              placeholder="e.g. Asthma — inhaler in the kit bag. Severe nut allergy, carries an EpiPen."
              className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">
              {form.medical_conditions.length}/{CONDITIONS_MAX}
            </p>
          </div>

          {/* A SEPARATE field, deliberately (#287 §3). A hearing impairment
              and a nut allergy are not the same kind of thing, they are
              acted on differently, and lumping them together reads badly
              to the person filling the form in. */}
          <div>
            <Label htmlFor="health-needs">Disability or accessibility needs</Label>
            <Textarea
              id="health-needs"
              data-testid="health-needs"
              value={form.accessibility_needs}
              onChange={(e) => set("accessibility_needs", e.target.value)}
              maxLength={NEEDS_MAX}
              rows={4}
              placeholder="e.g. Hearing aid in the left ear — face him when giving instructions."
              className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">
              {form.accessibility_needs.length}/{NEEDS_MAX}
            </p>
          </div>

          <Separator />

          {auditLine && (
            <p className="text-xs text-slate-500 break-words" data-testid="health-audit-line">
              {auditLine}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            {/* D186.7/R-003: the one gold action inside this open fold. */}
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="h-10"
              style={r5 ? { background: HEALTH_GOLD, color: HEALTH_INK } : { background: HEALTH_NAVY }}
              data-testid="health-save"
            >
              {saving ? "Saving…" : "Save details"}
            </Button>
            {/* Clearing is a first-class action, not something a player has
                to discover by deleting six fields by hand. It only stages
                the blanks — Save is still the thing that writes, so a
                mis-tap is one Cancel-by-navigation away from harmless. */}
            {/* Screen gap (#989 round 2): navy #0B2E59 text/icon on a
                #CBD5E1 border under r5, as drawn — not on D55's
                destructive list (it only stages a blank form; Save is
                still the write), so the shipped outline's own
                (Tailwind-default) border/text colour is what moves, not
                the destructive shape. */}
            <Button
              onClick={clearAll}
              variant="outline"
              className="h-10"
              style={r5 ? { color: HEALTH_NAVY, borderColor: "#CBD5E1" } : undefined}
              disabled={saving}
              data-testid="health-clear"
            >
              Clear all fields
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Clearing blanks the fields here; press Save to remove them from what your coaches see.
          </p>
          {/* D57 (#989 round 2, F4): a save failure renders here under r5,
              never a toast. */}
          {r5 && saveError && <AlertCallout testId="health-save-error">{saveError}</AlertCallout>}
        </>
      )}
    </SectionCard>
  );
}
